var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AsciiText } from './framework/AsciiText';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class NovoTouch {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        this.codeOffset = 0x30; // offset for firebase
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x01, 0x02, 0x00]), /\xAA\xBB\xCC\x80([\S\s])\x00[\S\s]\xDD\xEE/, tags);
                return match[1].charCodeAt(0) === 0;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // The device is unresponsive for a long time while changing power, so we suspend
                // polling and stall this handler to prevent other commands from executing.
                let match;
                this.state.busy.update(true);
                this.framework.suspendPolling(25000);
                try {
                    match = yield this.socket.send(this.makeCommand([0x01, power ? 0 : 1, 0x00]), /\xAA\xBB\xCC(?:\x01|\x80)([\S\s])\x00[\S\s]\xDD\xEE/, tags, { sendTimeoutMs: 5000 });
                }
                catch (error) {
                    this.state.busy.update(false);
                    throw error;
                }
                this.framework.suspend(20000); // don't send commands until device becomes responsive
                this.busyTimeout.set(() => this.state.busy.update(false), 20000);
                return match[1].charCodeAt(0) === 0;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x02, 0x00, 0x00]), /\xAA\xBB\xCC\x81([\S\s])\x00[\S\s]\xDD\xEE/, tags);
                return String.fromCharCode(match[1].charCodeAt(0) + this.codeOffset);
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x02, code.charCodeAt(0) - this.codeOffset, 0x00]), /\xAA\xBB\xCC(?:\x02|\x81)([\S\s])\x00[\S\s]\xDD\xEE/, tags, { sendTimeoutMs: 2000 });
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 4000);
                return String.fromCharCode(match[1].charCodeAt(0) + this.codeOffset);
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x03, 0x02, 0x00]), /\xAA\xBB\xCC\x82\x00([\S\s])[\S\s]\xDD\xEE/, tags);
                return match[1].charCodeAt(0);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x03, 0x00, volume]), /\xAA\xBB\xCC(?:\x03|\x82)\x00([\S\s])[\S\s]\xDD\xEE/, tags);
                return match[1].charCodeAt(0);
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x03, 0x03, 0x00]), /\xAA\xBB\xCC\x82\x01([\S\s])[\S\s]\xDD\xEE/, tags);
                return match[1].charCodeAt(0) === 0;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(this.makeCommand([0x03, 0x01, mute ? 0 : 1]), /\xAA\xBB\xCC(?:\x03|\x82)\x01([\S\s])[\S\s]\xDD\xEE/, tags);
                return match[1].charCodeAt(0) === 0;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 4000, controls: ['power', 'videoInput', 'volume', 'volumeMute'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: '\xAA\xBB\xCC\x01\x02\x00\x03\xDD\xEE',
                    next: /\xAA\xBB\xCC\x80[\S\s]\x00[\S\s]\xDD\xEE/,
                },
            ], credentials);
        });
    }
    // other class methods
    makeCommand(data) {
        const bytes = [0xaa, 0xbb, 0xcc];
        bytes.push(...data);
        const checksum = data.reduce((a, b) => a + b);
        bytes.push(checksum & 0x00ff, 0xdd, 0xee);
        return AsciiText.decode(bytes);
    }
}
// static properties
NovoTouch.driverType = 'socketDriver';
NovoTouch.key = '-KtY-ZdRD_Qao8JBne3Y';
NovoTouch.socketOptions = [
    {
        commandExtractor: {
            terminators: ['\xFF'],
        },
        idleTimeoutMs: 25000,
        isBinary: true,
        sendTerminator: '\xFF',
        port: 4999,
    },
];
